import type { ProgressChart } from "@/domains/progress/models/types";
import { formatProgressScore } from "@/domains/progress/utils/chart-copy";

const MS_PER_DAY = 86_400_000;

/** `day` is days since the baseline: fixed calendar offsets, never evenly spaced slots. */
const AXIS_TICKS = [
  { day: 0, label: "Start", widthPx: 34 },
  { day: 14, label: "Wk 2", widthPx: 35 },
  { day: 42, label: "Wk 6", widthPx: 36 },
  { day: 70, label: "Wk 10", widthPx: 43 },
  { day: 91, label: "3 mo", widthPx: 33 },
] as const;

const AXIS_MIN_SPAN_DAYS = 91;

/** Room at the left and right edges for the first and last value label. */
const X_INSET = 4;

/** Room above the scale ceiling for a value label and the "You Are Here" pill. */
const Y_INSET_TOP = 26;

/** Room below the scale floor for the skipped-check-in markers. */
const Y_INSET_BOTTOM = 10;

const TICK_LABEL_PADDING_PX = 6;

/** Damped Catmull-Rom; control points are also clamped, damping alone overshoots. */
const SMOOTHING = 0.8;

/** A `text-sm` semibold digit and decimal point, measured the way AXIS_TICKS widths were. */
const VALUE_LABEL_DIGIT_PX = 8;
const VALUE_LABEL_POINT_PX = 4;
const VALUE_LABEL_PADDING_PX = 6;

export type ProgressChartNode = {
  isLatest: boolean;
  key: string;
  value: number;
  x: number;
  y: number;
};

/** One unbroken run of answered readings. A skipped check-in starts a new one. */
export type ProgressChartSegment = {
  areaPath: string | null;
  key: string;
  linePath: string;
};

export type ProgressChartTick = {
  label: string;
  widthPx: number;
  x: number;
};

export type ProgressChartIdealSpan = {
  endX: number;
  startX: number;
};

export type ProgressChartGeometry = {
  ceilY: number;
  floorY: number;
  idealSpan: ProgressChartIdealSpan;
  idealY: number | null;
  nodes: ProgressChartNode[];
  segments: ProgressChartSegment[];
  /** Check-ins that were submitted with this question unanswered. */
  skippedMarkers: { key: string; x: number }[];
  ticks: ProgressChartTick[];
};

type Slot = {
  epoch: number;
  isBaseline: boolean;
  key: string;
  value: number | null;
};

type AnsweredSlot = Slot & { value: number };

const toEpoch = (iso: string) => {
  const epoch = new Date(iso).getTime();

  return Number.isFinite(epoch) ? epoch : null;
};

const round = (value: number) => {
  return Math.round(value * 100) / 100;
};

const isAnsweredSlot = (slot: Slot): slot is AnsweredSlot => {
  return slot.value !== null;
};

/** Points keep the payload order: the contract guarantees ascending `recordedAt`. */
const buildSlots = (chart: ProgressChart): Slot[] => {
  const slots: Slot[] = [];
  const baselineEpoch = chart.baseline ? toEpoch(chart.baseline.recordedAt) : null;

  if (chart.baseline && baselineEpoch !== null) {
    slots.push({
      epoch: baselineEpoch,
      isBaseline: true,
      key: `${chart.key}-baseline`,
      value: chart.baseline.value,
    });
  }

  chart.points.forEach((point, index) => {
    const epoch = toEpoch(point.recordedAt);

    if (epoch === null) {
      return;
    }

    slots.push({
      epoch,
      isBaseline: false,
      key: `${chart.key}-point-${index}-${point.checkInId}`,
      value: point.value,
    });
  });

  return slots;
};

const toSmoothPath = (nodes: ProgressChartNode[], ceilY: number, floorY: number) => {
  /** A cubic stays inside its own control points, so clamping them keeps the curve on the scale. */
  const clampY = (value: number) => {
    return round(Math.min(Math.max(value, ceilY), floorY));
  };

  const [first] = nodes;
  let path = `M ${first.x} ${first.y}`;

  for (let index = 0; index < nodes.length - 1; index += 1) {
    const previous = nodes[index - 1] ?? nodes[index];
    const start = nodes[index];
    const end = nodes[index + 1];
    const next = nodes[index + 2] ?? end;

    const firstControlX = round(start.x + ((end.x - previous.x) / 6) * SMOOTHING);
    const firstControlY = clampY(start.y + ((end.y - previous.y) / 6) * SMOOTHING);
    const secondControlX = round(end.x - ((next.x - start.x) / 6) * SMOOTHING);
    const secondControlY = clampY(end.y - ((next.y - start.y) / 6) * SMOOTHING);

    path += ` C ${firstControlX} ${firstControlY}, ${secondControlX} ${secondControlY}, ${end.x} ${end.y}`;
  }

  return path;
};

const toSegment = (
  nodes: ProgressChartNode[],
  ceilY: number,
  floorY: number,
): ProgressChartSegment => {
  const linePath = toSmoothPath(nodes, ceilY, floorY);
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const isLoneReading = nodes.length === 1;
  const areaPath = isLoneReading
    ? null
    : `${linePath} L ${last.x} ${floorY} L ${first.x} ${floorY} Z`;

  return { areaPath, key: first.key, linePath };
};

/** Every coordinate is a percentage of the plot box: 0–100 on both axes. */
export const buildProgressChartGeometry = (chart: ProgressChart): ProgressChartGeometry => {
  const floorY = 100 - Y_INSET_BOTTOM;
  const ceilY = Y_INSET_TOP;
  const { max, min } = chart.scale;
  const scaleSpan = max - min;

  const toY = (value: number) => {
    if (scaleSpan <= 0) {
      return (ceilY + floorY) / 2;
    }

    const clamped = Math.min(Math.max(value, min), max);

    return round(ceilY + ((max - clamped) / scaleSpan) * (floorY - ceilY));
  };

  const slots = buildSlots(chart);
  const originEpoch = slots.length > 0 ? slots[0].epoch : null;

  if (originEpoch === null) {
    return {
      ceilY,
      floorY,
      idealSpan: { endX: 100, startX: 0 },
      idealY: chart.idealLine ? toY(chart.idealLine.idealScore) : null,
      nodes: [],
      segments: [],
      skippedMarkers: [],
      ticks: [],
    };
  }

  const epochs = slots.map((slot) => {
    return slot.epoch;
  });
  /* Nothing guarantees the baseline predates every check-in, so the axis spans the readings too. */
  const startEpoch = Math.min(...epochs, originEpoch);
  const endEpoch = Math.max(...epochs, originEpoch + AXIS_MIN_SPAN_DAYS * MS_PER_DAY);

  const toX = (epoch: number) => {
    return round(X_INSET + ((epoch - startEpoch) / (endEpoch - startEpoch)) * (100 - X_INSET * 2));
  };

  const idealStartX = toX(originEpoch);
  const axisEndX = toX(endEpoch);
  const horizonEpoch = chart.idealLine ? toEpoch(chart.idealLine.horizonAt) : null;
  const idealSpan: ProgressChartIdealSpan = {
    endX:
      horizonEpoch === null
        ? axisEndX
        : Math.min(Math.max(toX(horizonEpoch), idealStartX), axisEndX),
    startX: idealStartX,
  };

  /* Matched by position, not `checkInId`: a duplicated id would mark two nodes as latest. */
  const answeredSlots = slots.filter((slot) => {
    return isAnsweredSlot(slot) && !slot.isBaseline;
  });
  const hasLatestReading = chart.latest !== null && chart.latest.value !== null;
  const latestKey =
    hasLatestReading && answeredSlots.length > 0
      ? answeredSlots[answeredSlots.length - 1].key
      : null;

  const nodes: ProgressChartNode[] = [];
  const segments: ProgressChartSegment[] = [];
  const skippedMarkers: { key: string; x: number }[] = [];
  let run: ProgressChartNode[] = [];

  const closeRun = () => {
    if (run.length > 0) {
      segments.push(toSegment(run, ceilY, floorY));
      run = [];
    }
  };

  slots.forEach((slot) => {
    if (!isAnsweredSlot(slot)) {
      skippedMarkers.push({ key: slot.key, x: toX(slot.epoch) });
      closeRun();

      return;
    }

    const node: ProgressChartNode = {
      isLatest: slot.key === latestKey,
      key: slot.key,
      value: slot.value,
      x: toX(slot.epoch),
      y: toY(slot.value),
    };

    nodes.push(node);
    run.push(node);
  });

  closeRun();

  const ticks: ProgressChartTick[] = [];

  AXIS_TICKS.forEach((tick) => {
    const x = toX(originEpoch + tick.day * MS_PER_DAY);

    if (x > 100) {
      return;
    }

    ticks.push({ label: tick.label, widthPx: tick.widthPx, x });
  });

  return {
    ceilY,
    floorY,
    idealSpan,
    idealY: chart.idealLine ? toY(chart.idealLine.idealScore) : null,
    nodes,
    segments,
    skippedMarkers,
    ticks,
  };
};

/** Centred labels overlap when their gap is under half of each width; the later one drops. */
export const filterVisibleTicks = (ticks: ProgressChartTick[], plotWidth: number) => {
  if (plotWidth <= 0) {
    return ticks;
  }

  const visible: ProgressChartTick[] = [];

  ticks.forEach((tick) => {
    const previous = visible[visible.length - 1];

    if (previous !== undefined) {
      const gapPx = ((tick.x - previous.x) / 100) * plotWidth;
      const requiredGapPx = previous.widthPx / 2 + tick.widthPx / 2 + TICK_LABEL_PADDING_PX;

      if (gapPx < requiredGapPx) {
        return;
      }
    }

    visible.push(tick);
  });

  return visible;
};

const valueLabelWidthPx = (value: number) => {
  return [...formatProgressScore(value)].reduce((width, character) => {
    return width + (character === "." ? VALUE_LABEL_POINT_PX : VALUE_LABEL_DIGIT_PX);
  }, 0);
};

const isValueLabelCrowded = (
  previous: ProgressChartNode | undefined,
  node: ProgressChartNode,
  plotWidth: number,
) => {
  if (previous === undefined) {
    return false;
  }

  const gapPx = ((node.x - previous.x) / 100) * plotWidth;
  const requiredGapPx =
    valueLabelWidthPx(previous.value) / 2 +
    valueLabelWidthPx(node.value) / 2 +
    VALUE_LABEL_PADDING_PX;

  return gapPx < requiredGapPx;
};

/** Keys of the nodes whose value label fits. The latest keeps its label even when crowded. */
export const filterVisibleValueLabels = (nodes: ProgressChartNode[], plotWidth: number) => {
  if (plotWidth <= 0) {
    return new Set(
      nodes.map((node) => {
        return node.key;
      }),
    );
  }

  const kept: ProgressChartNode[] = [];

  nodes.forEach((node) => {
    const previous = kept[kept.length - 1];
    const isCrowded = isValueLabelCrowded(previous, node, plotWidth);
    const mustEvictPrevious = isCrowded && node.isLatest;

    if (isCrowded && !mustEvictPrevious) {
      return;
    }

    if (mustEvictPrevious) {
      kept.pop();
    }

    kept.push(node);
  });

  return new Set(
    kept.map((node) => {
      return node.key;
    }),
  );
};
